import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import SessionContext from '../../../library/Core/SessionContext';
import MentorshipMatchingMenteeCard from './MentorshipMatchingMenteeCard';
import MentorshipService, {
    MATCHING_ASSIGNMENT_STATUS_FILTERS,
    MATCHING_BACKGROUND_BANNER,
    MATCHING_GATE_LOCKED_COPY,
    MATCHING_IA_LIMIT_MESSAGE,
    MATCHING_RUN_POLL_MS,
    MentorshipCampusOption,
    MentorshipCohortDetail,
    MentorshipMatchingGate,
    MentorshipMatchingPair,
    MentorshipMatchingPairListQuery,
    MentorshipMatchingRun,
    MentorshipMatchingStats,
    MentorshipProgramOption,
    MentorshipTaxonomyTrack,
} from './MentorshipService';

const INSTITUTE_ADMIN_ROLE = 'INSTITUTE_ADMIN';
const SUPER_ADMIN_ROLE = 'SUPER_ADMIN';
const ACTIVE_RUN_STATUSES = ['QUEUED', 'RUNNING'];

interface MentorshipMatchingProps {
    cohortId: number | null;
    disabled?: boolean;
}

const emptyQuery = (): MentorshipMatchingPairListQuery => ({
    search: '',
    campusId: null,
    programIds: [],
    years: [],
    trackIds: [],
    assignmentStatus: [],
});

const toggleNumber = (values: number[], value: number) =>
    values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

const toggleString = (values: string[], value: string) =>
    values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

const activeRunStorageKey = (cohortId: number) => `mentorship.matching.activeRun.${cohortId}`;

const readStoredRunId = (cohortId: number): number | null => {
    try {
        return MentorshipService.toNumberOrNull(window.sessionStorage.getItem(activeRunStorageKey(cohortId)));
    } catch {
        return null;
    }
};

const writeStoredRunId = (cohortId: number, runId: number | null) => {
    try {
        const key = activeRunStorageKey(cohortId);
        if (runId == null) window.sessionStorage.removeItem(key);
        else window.sessionStorage.setItem(key, String(runId));
    } catch {
        // sessionStorage may be unavailable; poll still works in this session
    }
};

const groupPairsByMentee = (pairs: MentorshipMatchingPair[]) => {
    const groups = new Map<number, MentorshipMatchingPair[]>();
    for (const pair of pairs) {
        const menteeId = pair.mentee?.id;
        if (menteeId == null) continue;
        const current = groups.get(menteeId) || [];
        current.push(pair);
        groups.set(menteeId, current);
    }
    return Array.from(groups.entries()).map(([menteeId, options]) => ({
        menteeId,
        options: [...options].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0)).slice(0, 5),
    }));
};

const gatePercent = (gate: MentorshipMatchingGate): number | null => {
    if (gate.threshold == null || gate.threshold <= 0) return null;
    return Math.round((gate.confirmedOrOnboardedMentors / gate.threshold) * 100);
};

const mentorsNeededForThreshold = (gate: MentorshipMatchingGate): number | null => {
    if (gate.threshold == null) return null;
    return Math.max(0, gate.threshold - gate.confirmedOrOnboardedMentors);
};

const MentorshipMatching = ({ cohortId, disabled = false }: MentorshipMatchingProps) => {
    const { sessionState } = useContext(SessionContext);
    const { globalState } = useContext(GlobalContext);
    const isInstituteAdmin = sessionState?.userTypeCode === INSTITUTE_ADMIN_ROLE;
    const isSuperAdmin = sessionState?.userTypeCode === SUPER_ADMIN_ROLE;

    const [cohortDetail, setCohortDetail] = useState<MentorshipCohortDetail | null>(null);
    const [tracks, setTracks] = useState<MentorshipTaxonomyTrack[]>([]);
    const [gate, setGate] = useState<MentorshipMatchingGate | null>(null);
    const [stats, setStats] = useState<MentorshipMatchingStats | null>(null);
    const [activeRun, setActiveRun] = useState<MentorshipMatchingRun | null>(null);
    const [pairs, setPairs] = useState<MentorshipMatchingPair[]>([]);
    const [pairsRunId, setPairsRunId] = useState<number | null>(null);
    const [draft, setDraft] = useState<MentorshipMatchingPairListQuery>(emptyQuery());
    const [query, setQuery] = useState<MentorshipMatchingPairListQuery>(emptyQuery());
    const [isLoadingMeta, setIsLoadingMeta] = useState(false);
    const [isLoadingPairs, setIsLoadingPairs] = useState(false);
    const [isActing, setIsActing] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [iaLimitReached, setIaLimitReached] = useState(false);
    const [showConfirmAll, setShowConfirmAll] = useState(false);
    const [showPublish, setShowPublish] = useState(false);

    const cohortAvailable = cohortId != null && !disabled;
    const runInProgress = Boolean(activeRun && ACTIVE_RUN_STATUSES.includes(activeRun.status || ''));
    // Hide Run chrome after a COMPLETED run exists (stats.currentRunId). QA: do not show again on refresh.
    const hasCompletedMatchingRun = stats?.currentRunId != null;
    const canRun =
        Boolean(gate?.unlocked) && !runInProgress && !isActing && !hasCompletedMatchingRun;
    const confirmationPercent = gate ? gatePercent(gate) : null;
    const mentorsStillNeeded = gate ? mentorsNeededForThreshold(gate) : null;

    const campuses: MentorshipCampusOption[] = useMemo(() => {
        const instituteId = MentorshipService.requestInstituteId;
        const all = isSuperAdmin
            ? MentorshipService.getGroupCampusesFromDomain(globalState?.domain, instituteId)
            : MentorshipService.getAdminCampuses(globalState?.domain, sessionState?.institutes);
        const associated = new Set(cohortDetail?.associatedCampusIds || []);
        if (associated.size === 0) return all;
        return all.filter((campus) => associated.has(campus.value));
    }, [globalState?.domain, sessionState?.institutes, isSuperAdmin, cohortDetail]);

    const isGroup = campuses.length > 1;
    const associatedCampusIds = cohortDetail?.associatedCampusIds?.length
        ? cohortDetail.associatedCampusIds
        : campuses.map((campus) => campus.value);
    const programs: MentorshipProgramOption[] = useMemo(
        () => MentorshipService.programsForCampuses(globalState?.domain, associatedCampusIds),
        [globalState?.domain, associatedCampusIds]
    );
    const uniquePrograms = useMemo(() => {
        const seen = new Set<number>();
        const items: Array<{ programId: number; name: string }> = [];
        for (const program of programs) {
            if (seen.has(program.programId)) continue;
            seen.add(program.programId);
            items.push({ programId: program.programId, name: program.name });
        }
        return items;
    }, [programs]);
    const yearOptions = useMemo(
        () =>
            MentorshipService.menteePoolConfiguredYearValues(
                (cohortDetail?.campuses || []).flatMap((campus) => campus.eligiblePrograms || [])
            ),
        [cohortDetail]
    );
    const queryRef = useRef(query);
    queryRef.current = query;
    const menteeGroups = useMemo(() => groupPairsByMentee(pairs), [pairs]);
    const pendingSelectedCount = useMemo(
        () =>
            menteeGroups.filter((group) =>
                group.options.some((option) => option.isSelected && option.assignmentStatus === 'Pending Decision')
            ).length,
        [menteeGroups]
    );
    const confirmedPairingsCount = stats?.confirmedPairings ?? 0;
    const canConfirmAll =
        hasCompletedMatchingRun && !isActing && !isLoadingPairs && pendingSelectedCount > 0;
    const canPublish = hasCompletedMatchingRun && !isActing && confirmedPairingsCount > 0;
    const filtersActive = MentorshipService.hasMatchingFilters(query);

    const loadPairs = async (id: number, nextQuery: MentorshipMatchingPairListQuery) => {
        setIsLoadingPairs(true);
        try {
            const result = await MentorshipService.listMatchingPairs(id, nextQuery);
            setPairs(result.pairs);
            setPairsRunId(result.runId);
        } catch (error) {
            setPairs([]);
            setPairsRunId(null);
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsLoadingPairs(false);
        }
    };

    const loadMeta = async (id: number) => {
        setIsLoadingMeta(true);
        setErrorMessage('');
        try {
            const [detail, taxonomy, nextGate, nextStats] = await Promise.all([
                MentorshipService.getCohort(id),
                MentorshipService.getDomainTaxonomy(id),
                MentorshipService.getMatchingGate(id),
                MentorshipService.getMatchingStats(id),
            ]);
            setCohortDetail(detail);
            setTracks(taxonomy.tracks || []);
            setGate(nextGate);
            setStats(nextStats);
        } catch (error) {
            setCohortDetail(null);
            setTracks([]);
            setGate(null);
            setStats(null);
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsLoadingMeta(false);
        }
    };

    const refreshAfterMutation = async (id: number) => {
        await Promise.all([loadMeta(id), loadPairs(id, queryRef.current)]);
    };

    useEffect(() => {
        if (cohortId == null || disabled) {
            setCohortDetail(null);
            setTracks([]);
            setGate(null);
            setStats(null);
            setActiveRun(null);
            setPairs([]);
            setPairsRunId(null);
            setDraft(emptyQuery());
            setQuery(emptyQuery());
            setErrorMessage('');
            setInfoMessage('');
            setIaLimitReached(false);
            setShowConfirmAll(false);
            setShowPublish(false);
            return;
        }
        const next = emptyQuery();
        setDraft(next);
        setQuery(next);
        setInfoMessage('');
        setIaLimitReached(false);
        loadMeta(cohortId);
        loadPairs(cohortId, next);
        const storedRunId = readStoredRunId(cohortId);
        if (storedRunId != null) {
            MentorshipService.getMatchingRun(cohortId, storedRunId)
                .then((run) => {
                    if (ACTIVE_RUN_STATUSES.includes(run.status || '')) {
                        setActiveRun(run);
                    } else {
                        writeStoredRunId(cohortId, null);
                        if (run.status === 'FAILED') {
                            setActiveRun(run);
                        } else {
                            setActiveRun(null);
                        }
                    }
                })
                .catch((error) => {
                    writeStoredRunId(cohortId, null);
                    setErrorMessage(MentorshipService.getErrorMessage(error));
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cohortId, disabled]);

    useEffect(() => {
        if (cohortId == null || !activeRun?.id || !ACTIVE_RUN_STATUSES.includes(activeRun.status || '')) {
            return undefined;
        }
        const timer = window.setTimeout(async () => {
            try {
                const next = await MentorshipService.getMatchingRun(cohortId, activeRun.id);
                setActiveRun(next);
                if (ACTIVE_RUN_STATUSES.includes(next.status || '')) {
                    writeStoredRunId(cohortId, next.id);
                    return;
                }
                writeStoredRunId(cohortId, null);
                if (next.status === 'COMPLETED') {
                    setInfoMessage(`Matching run ${next.id} COMPLETED.`);
                    await refreshAfterMutation(cohortId);
                }
            } catch (error) {
                setErrorMessage(MentorshipService.getErrorMessage(error));
            }
        }, MATCHING_RUN_POLL_MS);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cohortId, activeRun?.id, activeRun?.status]);

    const applyDraftFilters = () => {
        if (cohortId == null) return;
        const next = { ...draft };
        setQuery(next);
        loadPairs(cohortId, next);
    };

    const handleRun = async () => {
        if (cohortId == null || !canRun) return;
        setIsActing(true);
        setErrorMessage('');
        setInfoMessage('');
        try {
            const run = await MentorshipService.enqueueMatchingRun(cohortId);
            setActiveRun(run);
            writeStoredRunId(cohortId, run.id);
            const nextGate = await MentorshipService.getMatchingGate(cohortId);
            setGate(nextGate);
        } catch (error) {
            const message = MentorshipService.getErrorMessage(error);
            setErrorMessage(message);
            if (message === MATCHING_IA_LIMIT_MESSAGE) setIaLimitReached(true);
            try {
                const nextGate = await MentorshipService.getMatchingGate(cohortId);
                setGate(nextGate);
            } catch {
                // keep the enqueue error as the surface
            }
        } finally {
            setIsActing(false);
        }
    };

    const handleSelect = async (pairId: number) => {
        if (cohortId == null) return;
        setIsActing(true);
        setErrorMessage('');
        try {
            await MentorshipService.selectMatchingPair(cohortId, pairId);
            await loadPairs(cohortId, query);
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
            await loadPairs(cohortId, query);
        } finally {
            setIsActing(false);
        }
    };

    const handleConfirmPair = async (pairId: number) => {
        if (cohortId == null) return;
        setIsActing(true);
        setErrorMessage('');
        try {
            await MentorshipService.confirmMatchingPair(cohortId, pairId);
            setInfoMessage('Pair confirmed.');
            await refreshAfterMutation(cohortId);
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsActing(false);
        }
    };

    const handleConfirmAll = async () => {
        if (cohortId == null) return;
        setIsActing(true);
        setErrorMessage('');
        try {
            const result = await MentorshipService.confirmAllMatchingPairs(cohortId, query);
            setShowConfirmAll(false);
            setInfoMessage(`Confirm All: confirmed ${result.confirmed}, skipped ${result.skipped}.`);
            await refreshAfterMutation(cohortId);
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsActing(false);
        }
    };

    const handlePublish = async () => {
        if (cohortId == null) return;
        setIsActing(true);
        setErrorMessage('');
        try {
            const result = await MentorshipService.publishMatchingPairs(cohortId);
            setShowPublish(false);
            const emails = result.emails == null || result.emails === '' ? '—' : result.emails;
            setInfoMessage(`Published ${result.published}. Pair emails: ${emails}.`);
            await refreshAfterMutation(cohortId);
        } catch (error) {
            setShowPublish(false);
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsActing(false);
        }
    };

    const handleExport = async () => {
        if (cohortId == null) return;
        setIsActing(true);
        setErrorMessage('');
        try {
            await MentorshipService.exportMatchingPairs(cohortId, query);
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsActing(false);
        }
    };

    if (!cohortAvailable) {
        return <div className="text-muted">Select a cohort to manage Matching.</div>;
    }

    const statsCards = [
        { label: 'Confirmed Mentors', value: stats?.confirmedMentors ?? 0, color: 'success' },
        { label: 'Registered Mentees', value: stats?.registeredMentees ?? 0, color: 'primary' },
        { label: 'Total Pairings', value: stats?.totalPairings ?? 0, color: 'secondary' },
        { label: 'Pending Decision', value: stats?.pendingDecision ?? 0, color: 'warning' },
        { label: 'Confirmed Pairings', value: stats?.confirmedPairings ?? 0, color: 'success' },
    ];

  

    return (
        <div>
            <div className="d-flex flex-wrap align-items-start justify-content-between mb-4 gap-3">
                <div>
                    <div className="font-22 font-600 text-dark">Matching</div>
                    <div className="font-13 text-secondary">
                        AI-suggested top-3 mentor options per mentee · Select one per mentee and publish to participants
                    </div>
                </div>

                <div className="d-flex gap-2">
                    {!hasCompletedMatchingRun ? (
                        <button
                            type="button"
                            className={
                                canRun
                                    ? 'btn btn-green text-white btn-sm font-13 font-700'
                                    : 'btn btn-outline-secondary btn-sm font-13 font-600 py-2 px-3 rounded-3 rounded-pill'
                            }
                            disabled={!canRun}
                            aria-disabled={!canRun}
                            title={
                                canRun
                                    ? 'Run matching'
                                    : gate?.unlocked
                                      ? 'Matching run unavailable'
                                      : 'Locked until the confirmation threshold is met'
                            }
                            onClick={handleRun}>
                            <span>{canRun ? 'Run matching' : '🔒 Run matching · Locked'}</span>
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm font-13 font-600 py-2 px-3 rounded-3 rounded-pill"
                        disabled={!canConfirmAll}
                        title={
                            canConfirmAll
                                ? 'Confirm all selected Pending Decision pairings'
                                : pendingSelectedCount === 0
                                  ? 'No selected pairings are in Pending Decision status.'
                                  : 'Confirm All is unavailable while matching data is loading.'
                        }
                        onClick={() => setShowConfirmAll(true)}>
                        Confirm All Selections
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm font-13 font-600 py-2 px-3 rounded-3 rounded-pill"
                        disabled={!canPublish}
                        title={
                            canPublish
                                ? 'Publish all confirmed pairings to participants'
                                : confirmedPairingsCount === 0
                                  ? 'No confirmed pairings to publish.'
                                  : 'Publish is unavailable while matching data is loading.'
                        }
                        onClick={() => setShowPublish(true)}>
                        Publish Confirmed
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm font-13 font-600 py-2 px-3 rounded-3 rounded-pill"
                        disabled={isActing}
                        onClick={handleExport}>
                        📥 Export
                    </button>
                </div>
            </div>

            {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
            {infoMessage && <div className="alert alert-info">{infoMessage}</div>}

            {runInProgress && <div className="alert alert-warning">{MATCHING_BACKGROUND_BANNER}</div>}
            {activeRun?.status === 'FAILED' && <div className="alert alert-danger">Matching run {activeRun.id} FAILED.</div>}

            {isLoadingMeta && !gate ? (
                <div className="text-muted mb-3">Loading matching gate and stats…</div>
            ) : (
                <>
                    <div className="card  rounded-3 mb-4 shadow-sm bg-white">
                        <div className="card-header py-3 d-flex gap-2 text-dark">
                            <span className="fs-3">🔒</span>
                            <div>
                                <h4 className="fs-6 mb-0 fw-semibold">Matching Algorithm — Confirmation threshold</h4>
                                <p className="font-14 text-secondary mb-0">
                                    Algorithm runs only when ≥70% of the target mentor pool is confirmed. This ensures meaningful match
                                    quality.
                                </p>
                            </div>
                            <div className="d-flex flex-wrap align-items-center justify-content-between ms-auto">
                                <div className="text-end">
                                    <div className="font-18 font-700 text-warning">
                                        {confirmationPercent != null ? `${confirmationPercent}%` : '—'}
                                    </div>
                                    <div className="font-11 text-secondary font-600">
                                        {gate && gate.threshold != null
                                            ? `${gate.confirmedOrOnboardedMentors} / ${gate.threshold} confirmed`
                                            : '—'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-body py-4">
                            

                            <div className="progress rounded-pill bg-secondary bg-opacity-10 mb-2 mt-2" style={{ height: '8px' }}>
                                <div
                                    className="progress-bar bg-warning rounded-pill"
                                    style={{ width: `${confirmationPercent != null ? Math.min(100, confirmationPercent) : 0}%` }}
                                    role="progressbar"></div>
                            </div>

                            <div className="d-flex justify-content-between font-11 text-secondary mb-4">
                                <span>0%</span>
                                <span className="text-warning font-700">
                                    ▲ Current: {confirmationPercent != null ? `${confirmationPercent}%` : '—'}
                                </span>
                                <span className="text-success font-700">Unlock at 70% →</span>
                                <span>100%</span>
                            </div>

                            

                            <div className="d-flex flex-column gap-2 mt-3 bg-secondary bg-opacity-10 p-3 rounded-2">
                                <div className="d-flex align-items-center flex-wrap gap-2">
                                    {!hasCompletedMatchingRun ? (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm font-12 font-700 py-1.5 px-3 rounded-2"
                                            disabled={!canRun}
                                            onClick={handleRun}>
                                            {canRun ? 'Run Matching Algorithm' : '🔒 Run Matching Algorithm · Locked'}
                                        </button>
                                    ) : null}
                                    <span className="font-14 text-dark ms-1">
                                        {hasCompletedMatchingRun
                                            ? 'Matching has already been run for this cohort.'
                                            : gate?.unlocked
                                            ? 'Gate unlocked.'
                                            : mentorsStillNeeded != null && mentorsStillNeeded > 0
                                                ? `Need ${mentorsStillNeeded} more confirmed mentors before matching can run.`
                                                : `Gate locked: ${MATCHING_GATE_LOCKED_COPY}.`}
                                    </span>
                                </div>

                                {gate?.requiredSum == null && (
                                    <div className="font-14 text-muted">
                                        Gate stays locked until every campus has mentee target and max mentees per mentor.
                                    </div>
                                )}
                                <div className="font-14 text-dark">
                                    {isInstituteAdmin && (
                                        <>
                                            Institute Admin: 1 run + 1 re-run for this cohort. Remaining:{' '}
                                            {MentorshipService.matchingInstituteAdminRemaining(gate?.iaRunCount)}.
                                            {iaLimitReached ? ` ${MATCHING_IA_LIMIT_MESSAGE}` : ''}
                                        </>
                                    )}
                                    {isSuperAdmin && <><b>Super Admin:</b> uncapped.</>}
                                    {!isInstituteAdmin && !isSuperAdmin && (
                                        <>Run access follows the existing Module Settings tenancy. No extra permission check.</>
                                    )}
                                </div>
                            </div>
                        </div>                        
                        
                    </div>

                    <div className="d-flex flex-nowrap overflow-auto gap-3 mb-3 pb-1">
                            {statsCards.map((card, index) => (
                                <div
                                    key={card.label}
                                    className={`card border-0 rounded-3 p-3 h-100 flex-1 bg-${card.color} bg-opacity-25`} style={{ minWidth: 190 }}>
                                    <div className={`fs-2 fw-bold lh-1 text-${card.color} text-opacity-10`}>{card.value}</div>
                                    <div className="small fw-bold text-muted text-uppercase tracking-wider font-11 mb-0">{card.label}</div>
                                </div>
                            ))}
                        </div>

                    
                </>
            )}

            <fieldset disabled={isActing}>
                <div className="card border-0 rounded-3 p-3 shadow-sm bg-white mb-3">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1">
                            <div className="position-relative" style={{ minWidth: 220 }}>
                                <input
                                    className="form-control form-search font-14 border-light-subtle"
                                    placeholder="Search name, email, or UUID"
                                    value={draft.search || ''}
                                    onChange={(event) => setDraft((current) => ({ ...current, search: event.target.value }))}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            applyDraftFilters();
                                        }
                                    }}
                                />
                            </div>

                            {isGroup && (
                                <select
                                    className="form-select font-12 w-auto"
                                    value={draft.campusId ?? ''}
                                    onChange={(event) =>
                                        setDraft((current) => ({
                                            ...current,
                                            campusId: event.target.value ? Number(event.target.value) : null,
                                        }))
                                    }>
                                    <option value="">All campuses</option>
                                    {campuses.map((campus) => (
                                        <option key={campus.value} value={campus.value}>
                                            {campus.label}
                                        </option>
                                    ))}
                                </select>
                            )}

                            {uniquePrograms.length > 0 && (
                                <select
                                    className="form-select font-12 w-auto"
                                    value={(draft.programIds || []).map(String)}
                                    onChange={(e) => {
                                        const selectedIds = Array.from(e.target.selectedOptions).map((option) => Number(option.value));
                                        setDraft((current) => ({
                                            ...current,
                                            programIds: selectedIds,
                                        }));
                                    }}>
                                    <option value="">Program</option>
                                    {uniquePrograms.map((program) => (
                                        <option key={program.programId} value={program.programId}>
                                            {program.name}
                                        </option>
                                    ))}
                                </select>
                            )}

                            {yearOptions.length === 0 ? (
                                <div className="small text-muted">No stored eligible-program years for this cohort.</div>
                            ) : (
                                <select
                                    className="form-select font-12 w-auto"
                                    value={(draft.years || []).map(String)}
                                    onChange={(e) => {
                                        const selectedYears = Array.from(e.target.selectedOptions).map((option) => Number(option.value));
                                        setDraft((current) => ({
                                            ...current,
                                            years: selectedYears,
                                        }));
                                    }}>
                                    <option value="">Year</option>
                                    {yearOptions.map((year) => (
                                        <option key={year} value={year}>
                                            {MentorshipService.formatMenteePoolYearLabel(year)}
                                        </option>
                                    ))}
                                </select>
                            )}

                            {tracks.filter((track) => track.id != null).length === 0 ? (
                                <div className="small text-muted">No career tracks on this cohort taxonomy yet.</div>
                            ) : (
                                <select
                                    className="form-select font-12 w-auto"
                                    value={(draft.trackIds || []).map(String)}
                                    onChange={(e) => {
                                        const selectedIds = Array.from(e.target.selectedOptions).map((option) => Number(option.value));
                                        setDraft((current) => ({
                                            ...current,
                                            trackIds: selectedIds,
                                        }));
                                    }}>
                                    <option value="">Career Tracks</option>
                                    {tracks
                                        .filter((track) => track.id != null)
                                        .map((track) => (
                                            <option key={track.id} value={track.id as number}>
                                                {track.name}
                                            </option>
                                        ))}
                                </select>
                            )}

                            <select
                                className="form-select font-12 w-auto"
                                value={draft.assignmentStatus || []}
                                onChange={(e) => {
                                    const selectedStatuses = Array.from(e.target.selectedOptions).map((option) => option.value);
                                    setDraft((current) => ({
                                        ...current,
                                        assignmentStatus: selectedStatuses,
                                    }));
                                }}>
                                <option value="">Matching Status</option>
                                {MATCHING_ASSIGNMENT_STATUS_FILTERS.map((status) => (
                                    <option key={status} value={status}>
                                        {status}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button type="button" className="btn btn-outline-secondary font-12 font-600 rounded-1" onClick={applyDraftFilters}>
                            Apply filters
                        </button>
                    </div>
                </div>
            </fieldset>

            {isLoadingPairs ? (
                <div className="text-muted">Loading pairings…</div>
            ) : pairsRunId == null && pairs.length === 0 ? (
                <div className="text-muted">No completed matching run yet. Pairings will appear after a COMPLETED run.</div>
            ) : menteeGroups.length === 0 ? (
                <div className="text-muted">No pairings match the current filters.</div>
            ) : (
                menteeGroups.map((group) => (
                    <MentorshipMatchingMenteeCard
                        key={group.menteeId}
                        options={group.options}
                        campuses={campuses}
                        disabled={isActing || runInProgress}
                        busy={isActing}
                        onSelect={handleSelect}
                        onConfirm={handleConfirmPair}
                    />
                ))
            )}

            <Modal show={showConfirmAll} onHide={() => setShowConfirmAll(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm All Selections</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {filtersActive ? (
                        <p>
                            Confirm selected mentors for the currently filtered pairings? {pendingSelectedCount} mentee
                            {pendingSelectedCount === 1 ? '' : 's'} in the loaded list have a selected Pending Decision option.
                        </p>
                    ) : (
                        <p>
                            Confirm all default LLM selections? {pendingSelectedCount} mentee
                            {pendingSelectedCount === 1 ? '' : 's'} in the loaded list have a selected Pending Decision option.
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowConfirmAll(false)}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-success"
                        disabled={isActing || pendingSelectedCount === 0}
                        onClick={handleConfirmAll}>
                        Confirm
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={showPublish} onHide={() => setShowPublish(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Publish Confirmed</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Are you sure you want to publish all confirmed pairings? Confirmed pairings on the current run:{' '}
                        {stats?.confirmedPairings ?? 0}.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPublish(false)}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-success"
                        disabled={isActing || confirmedPairingsCount === 0}
                        onClick={handlePublish}>
                        Publish
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default MentorshipMatching;
